import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Paper,
  Divider
} from "@mui/material";
import {
  AdminPanelSettings,
  People,
  Settings,
  Description,
  Article,
  Dashboard,
  Assignment,
  Visibility,
  MonetizationOn,
  DocumentScanner,
  TrendingUp,
  Receipt,
  AddCircle,
  List as ListIcon,
  AssignmentTurnedIn,
  AttachMoney,
  AccountBalance,
} from "@mui/icons-material";
import { useUserContext } from './context/UserContext';
import { useLocation, useNavigate } from "react-router-dom";
import { getUserEmail } from "../utils/sessionCheck";

const HomePage = () => {
  const context = useUserContext();
  const { isAdmin, setIsAdmin, adminSelectedTemplate, adminSelectedQuotationTemplate } = context;
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const applicationURL = process.env.REACT_APP_JWT_URL;
  const applicationTokenDecodeURL = process.env.REACT_APP_JWT_DECODE;
  const SecuredMergedDecodeURL = applicationURL + applicationTokenDecodeURL;

  const JWTData = location.state?.JWTdata;
  const JWTtoken = JWTData?.token;
  const StateFromFirebase = location.state?.userAdminStatus;
 

  useEffect(() => {
    if (!JWTtoken) {
      setLoading(false);
      return;
    }
    handlestatusAndVerifyToken();
  }, []);

    // Super Admin check
  const userEmail = getUserEmail() || "";
  const superAdminEmails = process.env.REACT_APP_SUPER_ADMINS
    ? process.env.REACT_APP_SUPER_ADMINS.split(',').map(email => email.trim().toLowerCase())
    : [];
  const isSuperAdmin =
    userEmail &&
    superAdminEmails.includes(userEmail.trim().toLowerCase());

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography variant="h5">Loading...</Typography>
      </Box>
    );
  }

  if (isSuperAdmin) {
    return (
      <Box textAlign="center" mt={8}>
        <Typography variant="h4" color="error" gutterBottom>
          Welcome Super Admin!
        </Typography>
        <Typography variant="body1" mt={2} gutterBottom>
          You have super admin privileges. Please use the Tenant Management section.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ mt: 4 }}
          onClick={() => navigate("/TenantManagement")}
        >
          Go to Tenant Management
        </Button>
      </Box>
    );
  }

  const handlestatusAndVerifyToken = async () => {
    try {
      const response = await fetch(SecuredMergedDecodeURL, {
        method: 'GET',
        headers: { Authorization: `Bearer ${JWTtoken}` },
      });
      const data = await response.json();

      if (response.ok) {
        if (data.role === "admin" && StateFromFirebase === true) {
          setIsAdmin(true);
        } else if (data.role !== "admin" && StateFromFirebase === false) {
          setIsAdmin(false);
        }
      } else {
        // Error handling
      }
    } catch (error) {
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  // Navigation handlers matching Navbar logic
  const handleInvoiceNavigation = () => {
    if (isAdmin) {
      if (adminSelectedTemplate) {
        navigate(`/invoice-templates/${adminSelectedTemplate}`);
      } else {
        navigate('/invoice-templates');
      }
    } else {
      if (adminSelectedTemplate) {
        navigate(`/invoice-templates/${adminSelectedTemplate}`);
      }
    }
  };

  const handleQuotationNavigation = () => {
    if (isAdmin) {
      if (adminSelectedQuotationTemplate) {
        navigate(`/quotation-templates/${adminSelectedQuotationTemplate}`);
      } else {
        navigate('/quotation-templates');
      }
    } else {
      if (adminSelectedQuotationTemplate) {
        navigate(`/quotation-templates/${adminSelectedQuotationTemplate}`);
      }
    }
  };

  // Admin Panel Cards
  const adminPanelCards = [
    {
      title: "User Management",
      description: "Manage users, roles, and permissions",
      icon: <People sx={{ fontSize: 32, color: 'success.main' }} />,
      path: "/admin/user-management",
      color: "#2e7d32"
    },
    {
      title: "Invoice Templates",
      description: "Configure and manage invoice templates",
      icon: <Description sx={{ fontSize: 32, color: 'warning.main' }} />,
      path: "/invoice-templates",
      color: "#ed6c02"
    },
    {
      title: "Quotation Templates",
      description: "Configure and manage quotation templates",
      icon: <Article sx={{ fontSize: 32, color: 'error.main' }} />,
      path: "/quotation-templates",
      color: "#d32f2f"
    }
    
  ];

  const userCards = [
    {
      title: "Create Invoice",
      description: "Generate professional invoices for your business",
      icon: <Description sx={{ fontSize: 32, color: 'primary.main' }} />,
      action: handleInvoiceNavigation,
      color: "#1976d2"
    },
    {
      title: "Create Quotation",
      description: "Create quotations and estimates for clients",
      icon: <Article sx={{ fontSize: 32, color: 'success.main' }} />,
      action: handleQuotationNavigation,
      color: "#2e7d32"
    },
    {
      title: "Credit Note",
      description: "Create and manage credit notes",
      icon: <Receipt sx={{ fontSize: 32, color: 'info.main' }} />,
      action: () => navigate("/creditNote"),
      color: "#0288d1"
    },
    {
      title: "History",
      description: "Access your generated invoices and quotations",
      icon: <DocumentScanner sx={{ fontSize: 32, color: 'error.main' }} />,
      action: () => navigate("/GeneratedDocuments"),
      color: "#d32f2f"
    },
    {
      title: "Add Product",
      description: "Add new products to your catalog",
      icon: <AddCircle sx={{ fontSize: 32, color: '#1976d2' }} />,
      action: () => navigate("/AddProduct"),
      color: "#1976d2"
    },
    {
      title: "View Products",
      description: "Browse and manage your product catalog",
      icon: <ListIcon sx={{ fontSize: 32, color: '#ed6c02' }} />,
      action: () => navigate("/ViewProducts"),
      color: "#ed6c02"
    },
    {
      title: "View Expenses",
      description: "Track and manage your business expenses",
      icon: <MonetizationOn sx={{ fontSize: 32, color: '#1976d2' }} />,
      action: () => navigate("/accounting/expenses"),
      color: "#1976d2"
    },
    {
      title: "Statement",
      description: "View transaction statements and reports",
      icon: <DocumentScanner sx={{ fontSize: 32, color: '#9c27b0' }} />,
      action: () => navigate("/accounting/transactions"),
      color: "#9c27b0"
    },
    {
      title: "Bank Config",
      description: "Manage your bank configurations and branches",
      icon: <AccountBalance sx={{ fontSize: 32, color: '#1976d2' }} />,
      action: () => navigate("/accounting/config"),
      color: "#1976d2"
    },
    {
      title: "GST Reports",
      description: "Generate and manage GST reports",
      icon: <TrendingUp sx={{ fontSize: 32, color: '#ed6c02' }} />,
      action: () => navigate("/accounting/gst"),
      color: "#ed6c02"
    },
    {
      title: "Receivables",
      description: "Track and manage customer receivables",
      icon: <AssignmentTurnedIn sx={{ fontSize: 32, color: '#0288d1' }} />,
      action: () => navigate("/accounting/receivables"),
      color: "#0288d1",
      disabled: true,
    },
    {
      title: "Dues",
      description: "Monitor outstanding dues and payments",
      icon: <AttachMoney sx={{ fontSize: 32, color: '#7b1fa2' }} />,
      action: () => navigate("/accounting/dues"),
      color: "#7b1fa2",
      disabled: true,
    },
  ];

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography variant="h5">Loading...</Typography>
      </Box>
    );
  }

  // Styled Section Heading Component
  const SectionHeading = ({ title, variant = "primary" }) => {
    const isAdmin = variant === "admin";
    const borderColor = isAdmin ? 'secondary.main' : 'primary.main';
    const bgColor = isAdmin ? 'rgba(244, 67, 54, 0.08)' : 'rgba(33, 150, 243, 0.08)';
    const textColor = isAdmin ? 'secondary.main' : 'primary.main';
    const emoji = isAdmin ? '🔧' : '⚡';

    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 2, pl: 2, borderLeft: '4px solid', borderColor, backgroundColor: bgColor, py: 1.5, borderRadius: '4px' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: textColor, fontSize: '0.875rem', letterSpacing: '0.5px', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{emoji}</span>
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Box>
    );
  };

  return (
    <Box sx={{ padding: 3, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Welcome Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
          color: 'white',
          borderRadius: 2,
          py: 6,
          px: 2
        }}
      >
        <Typography variant="h3" gutterBottom>
          {isAdmin ? "Welcome Admin!" : "Welcome User"}
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9 }}>
          {isAdmin
            ? "Manage your organization with full administrative access"
            : "Create invoices and quotations for your business"
          }
        </Typography>
        {isAdmin && (
          <Box sx={{ mt: 2 }}>
            <Chip
              icon={<AdminPanelSettings />}
              label="Administrator"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Admin Panel */}
      {isAdmin && (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AdminPanelSettings color="primary" />
              Admin Panel
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Box>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            {adminPanelCards.map((card, index) => (
              <Grid item xs={6} sm={4} md={2.4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 1.5, px: 1.5 }}>
                    <Box sx={{ mb: 1 }}>
                      {card.icon}
                    </Box>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 0.75 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
                      {card.description}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', pb: 1.5, pt: 0 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate(card.path)}
                      sx={{
                        backgroundColor: card.color,
                        fontSize: '0.8125rem',
                        py: 0.6,
                        px: 2.5,
                        '&:hover': {
                          backgroundColor: card.color,
                          opacity: 0.8
                        }
                      }}
                    >
                      Access
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 4 }} />
        </>
      )}

      {/* Quick Actions Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Dashboard color="primary" />
          {isAdmin ? "Quick Actions" : "Your Dashboard"}
        </Typography>
        <Divider sx={{ mb: 3 }} />
      </Box>

      <Grid container spacing={2}>
        {userCards.map((card, index) => (
          <Grid item xs={6} sm={4} md={2.4} key={index}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: card.disabled ? 'not-allowed' : 'pointer',
                opacity: card.disabled ? 0.5 : 1,
                '&:hover': {
                  transform: card.disabled ? 'none' : 'translateY(-4px)',
                  boxShadow: card.disabled ? 'none' : '0 8px 25px rgba(0,0,0,0.15)'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, textAlign: 'center', py: 1.5, px: 1.5 }}>
                <Box sx={{ mb: 1 }}>
                  {card.icon}
                </Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', fontSize: '1rem', mb: 0.75 }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.4 }}>
                  {card.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', pb: 1.5, pt: 0 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={card.action}
                  disabled={card.disabled}
                  sx={{
                    backgroundColor: card.color,
                    fontSize: '0.8125rem',
                    py: 0.6,
                    px: 2.5,
                    '&:hover': {
                      backgroundColor: card.color,
                      opacity: 0.8
                    }
                  }}
                >
                  {card.title.includes('Create') ? 'Create' : 'View'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Admin Status Indicator */}
      {isAdmin && (
        <Paper elevation={1} sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5' }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminPanelSettings fontSize="small" />
            You are logged in with administrator privileges. You have access to all system features and configurations.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default HomePage;